import React, { useEffect, useState } from "react";
import { Link, useParams, useHistory } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import ReactApexChart from 'react-apexcharts';
import AccessPage from "../Shared/accessPage";

const RedTeaming = () => {
  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedAttackTypes, setSelectedAttackTypes] = useState([]);
  const [frequency, setFrequency] = useState("once");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [simulationHistory, setSimulationHistory] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalSimulations: 0,
    successful: 0,
    blocked: 0,
    lastRun: null
  });

  const { orgId } = useParams();
  const vendorId = sessionStorage.getItem('vendorId');
  const vendorName = sessionStorage.getItem('vendorName');
  const history = useHistory();

  // Mock data for demonstration
  const mockSimulationHistory = [
    {
      id: 1,
      date: "Jul 30, 2025",
      asset: "login.api.com",
      attackType: "Port Scan",
      status: "Blocked",
      duration: "2.3 min",
      details: "Simulated port scanning detected by firewall"
    },
    {
      id: 2,
      date: "Jul 29, 2025",
      asset: "test.srv.net",
      attackType: "SSH Brute Force",
      status: "Success",
      duration: "1.5 min",
      details: "Simulated credential compromise detected"
    },
    {
      id: 3,
      date: "Jul 28, 2025",
      asset: "staging.site.io",
      attackType: "Web Exploit",
      status: "Error",
      duration: "0.8 min",
      details: "Simulation failed due to network timeout"
    },
    {
      id: 4,
      date: "Jul 27, 2025",
      asset: "dev.example.com",
      attackType: "Misconfig Detection",
      status: "Success",
      duration: "3.2 min",
      details: "Security misconfiguration identified"
    },
    {
      id: 5,
      date: "Jul 26, 2025",
      asset: "api.service.net",
      attackType: "Subdomain Takeover",
      status: "Blocked",
      duration: "1.1 min",
      details: "DNS protection prevented takeover attempt"
    }
  ];

  const mockAssets = [
    { id: 1, name: "api.domain.com", type: "API Server" },
    { id: 2, name: "dev.site.io", type: "Development Server" },
    { id: 3, name: "staging.example.com", type: "Staging Server" },
    { id: 4, name: "login.company.com", type: "Authentication Server" },
    { id: 5, name: "admin.portal.net", type: "Admin Portal" }
  ];

  const attackTypes = [
    { id: "port_scan", name: "Port Scanning", description: "Simulate port scanning to identify open services" },
    { id: "ssh_brute", name: "SSH Brute Force", description: "Test SSH authentication security" },
    { id: "web_exploit", name: "Web Exploit (CVE Simulation)", description: "Simulate known web vulnerabilities" },
    { id: "misconfig", name: "Misconfig Detection", description: "Identify security misconfigurations" },
    { id: "subdomain_takeover", name: "Subdomain Takeover", description: "Test DNS and subdomain security" },
    { id: "vulnerability_probe", name: "Vulnerability Probing", description: "Probe for common vulnerabilities" }
  ];

  useEffect(() => {
    // Initialize with mock data
    setSimulationHistory(mockSimulationHistory);
    setSummaryData({
      totalSimulations: 18,
      successful: 6,
      blocked: 12,
      lastRun: "Jul 30, 2025"
    });
  }, [mockSimulationHistory]);

  const handleLaunchSimulation = () => {
    if (selectedAssets.length === 0) {
      toastr.error("Error", "Please select at least one target asset.");
      return;
    }
    if (selectedAttackTypes.length === 0) {
      toastr.error("Error", "Please select at least one attack type.");
      return;
    }

    // Mock API call
    const simulationData = {
      assets: selectedAssets,
      attackTypes: selectedAttackTypes,
      frequency: frequency,
      startDate: startDate,
      endDate: endDate
    };

    toastr.success("Success", "Red team simulation launched successfully!");
    
    // Reset form
    setSelectedAssets([]);
    setSelectedAttackTypes([]);
    setFrequency("once");
    setStartDate(null);
    setEndDate(null);
    setIsLaunchModalOpen(false);
  };

  const handleAssetToggle = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const handleAttackTypeToggle = (attackTypeId) => {
    setSelectedAttackTypes(prev => 
      prev.includes(attackTypeId) 
        ? prev.filter(id => id !== attackTypeId)
        : [...prev, attackTypeId]
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Success":
        return "danger"; // Red for successful exploitation
      case "Blocked":
        return "success"; // Green for blocked attacks
      case "Error":
        return "warning"; // Yellow for errors
      default:
        return "secondary";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Success":
        return "fa-check-circle";
      case "Blocked":
        return "fa-shield-alt";
      case "Error":
        return "fa-exclamation-triangle";
      default:
        return "fa-question-circle";
    }
  };

  const chartOptions = {
    chart: {
      type: 'donut',
    },
    labels: ['Successful', 'Blocked', 'Error'],
    colors: ['#dc3545', '#28a745', '#ffc107'],
    legend: {
      position: 'bottom'
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 300
        },
        legend: {
          position: 'bottom'
        }
      }
    }],
    tooltip: {
      y: {
        formatter: (value) => `${value} simulations`
      }
    }
  };

  const chartSeries = [summaryData.successful, summaryData.blocked, summaryData.totalSimulations - summaryData.successful - summaryData.blocked];

  return (
    <>
      <div className="container-fluid">
        <AccessPage></AccessPage>
        <div className="block-header">
          <div className="row clearfix">
            <div className="col-md-6 col-sm-12">
              <h1 className="welcome-text">Red Teaming Dashboard</h1>
              <nav aria-label="breadcrumb" className="mt-1">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    {vendorId ? (
                      <Link to="/vendor" onClick={() => sessionStorage.clear()}>
                        Vendor Assessment
                      </Link>
                    ) : (
                      <Link to="/" className="welcome-textSmall">Home</Link>
                    )}
                  </li>
                  {vendorName && (
                    <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                      <Link to={`/dashboard/vendor/${vendorId}`}>{vendorName}</Link>
                    </li>
                  )}
                  <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                    Red Teaming
                  </li>
                </ol>
              </nav>
            </div>
            <div className="col-md-6 col-sm-12 hidden-xs d-flex justify-content-end align-items-center">
              <Link
                to={`/red-teaming/settings${vendorId ? '/vendor/' + vendorId : ''}`}
                className="btn btn-outline-secondary mr-2"
              >
                <i className="fa fa-cog mr-1"></i>
                Settings
              </Link>
              <button
                onClick={() => setIsLaunchModalOpen(true)}
                className="btn btn-primary"
                style={{ color: "white", fontWeight: "bold" }}
              >
                + Launch Simulation
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row clearfix">
          <div className="col-lg-3 col-md-6">
            <div className="card">
              <div className="body">
                <div className="d-flex align-items-center">
                  <div className="icon-in-bg bg-indigo text-white rounded-circle">
                    <i className="fa fa-briefcase"></i>
                  </div>
                  <div className="ml-4">
                    <span>Total Simulations</span>
                    <h4 className="mb-0 font-weight-medium">{summaryData.totalSimulations}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card">
              <div className="body">
                <div className="d-flex align-items-center">
                  <div className="icon-in-bg bg-success text-white rounded-circle">
                    <i className="fa fa-check"></i>
                  </div>
                  <div className="ml-4">
                    <span>Successful</span>
                    <h4 className="mb-0 font-weight-medium">{summaryData.successful}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card">
              <div className="body">
                <div className="d-flex align-items-center">
                  <div className="icon-in-bg bg-warning text-white rounded-circle">
                    <i className="fa fa-shield-alt"></i>
                  </div>
                  <div className="ml-4">
                    <span>Blocked</span>
                    <h4 className="mb-0 font-weight-medium">{summaryData.blocked}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card">
              <div className="body">
                <div className="d-flex align-items-center">
                  <div className="icon-in-bg bg-info text-white rounded-circle">
                    <i className="fa fa-clock"></i>
                  </div>
                  <div className="ml-4">
                    <span>Last Run</span>
                    <h4 className="mb-0 font-weight-medium">{summaryData.lastRun}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="row clearfix">
          <div className="col-lg-5 col-md-12">
            <div className="card">
              <div className="body" style={{ minHeight: "400px" }}>
                <h6 className="welcome-textSmall mb-3">Simulation Results Overview</h6>
                <div className="row justify-content-center">
                  <div className="col-9">
                    <ReactApexChart
                      options={chartOptions}
                      series={chartSeries}
                      type="donut"
                      height={350}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-7 col-md-12">
            <div className="card">
              <div className="body" style={{ minHeight: "400px" }}>
                <h6 className="welcome-textSmall mb-3">Recent Activity</h6>
                <div className="timeline">
                  {simulationHistory.slice(0, 5).map((simulation, index) => (
                    <div key={simulation.id} className="timeline-item">
                      <div className="timeline-marker bg-primary"></div>
                      <div className="timeline-content">
                        <h6 className="timeline-title">{simulation.asset}</h6>
                        <p className="timeline-text">
                          {simulation.attackType} - {simulation.status}
                        </p>
                        <small className="text-muted">{simulation.date}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Simulation History */}
        <div className="card">
          <div className="header">
            <h2>Simulation History</h2>
            <div className="header-action">
              <button className="btn btn-sm btn-outline-secondary mr-2">
                <i className="fa fa-filter"></i> Filter
              </button>
              <button className="btn btn-sm btn-outline-secondary">
                <i className="fa fa-download"></i> Export
              </button>
            </div>
          </div>
          <div className="body">
            <div className="table-responsive">
              <table className="table table-custom spacing5 mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Asset</th>
                    <th>Attack Type</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {simulationHistory.map((simulation) => (
                    <tr key={simulation.id}>
                      <td>{simulation.date}</td>
                      <td>{simulation.asset}</td>
                      <td>{simulation.attackType}</td>
                      <td>
                        <span className={`badge badge-${getStatusColor(simulation.status)}`}>
                          <i className={`fa ${getStatusIcon(simulation.status)} mr-1`}></i>
                          {simulation.status}
                        </span>
                      </td>
                      <td>{simulation.duration}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            history.push(`/red-teaming/simulation/${simulation.id}`);
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex justify-content-between align-content-center mt-3">
              <div className="dataTables_paginate">
                <span>Showing 1 to 5 of {simulationHistory.length} results</span>
              </div>
              <div className="pagination-info">
                <span>Page 1 of 1</span>
              </div>
            </div>
          </div>
        </div>

        {/* Launch Simulation Modal */}
        {isLaunchModalOpen && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Launch Red Team Simulation</h5>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setIsLaunchModalOpen(false)}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    {/* Asset Selection */}
                    <div className="col-md-12 mb-4">
                      <h6>Select Target Assets</h6>
                      <div className="asset-list">
                        {mockAssets.map((asset) => (
                          <div key={asset.id} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`asset-${asset.id}`}
                              checked={selectedAssets.includes(asset.id)}
                              onChange={() => handleAssetToggle(asset.id)}
                            />
                            <label className="form-check-label" htmlFor={`asset-${asset.id}`}>
                              <strong>{asset.name}</strong> - {asset.type}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Attack Type Selection */}
                    <div className="col-md-12 mb-4">
                      <h6>Choose Attack Types</h6>
                      <div className="attack-types-list">
                        {attackTypes.map((attackType) => (
                          <div key={attackType.id} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`attack-${attackType.id}`}
                              checked={selectedAttackTypes.includes(attackType.id)}
                              onChange={() => handleAttackTypeToggle(attackType.id)}
                            />
                            <label className="form-check-label" htmlFor={`attack-${attackType.id}`}>
                              <strong>{attackType.name}</strong>
                              <br />
                              <small className="text-muted">{attackType.description}</small>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Frequency Selection */}
                    <div className="col-md-12 mb-4">
                      <h6>Frequency</h6>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="frequency"
                          id="frequency-once"
                          value="once"
                          checked={frequency === "once"}
                          onChange={(e) => setFrequency(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="frequency-once">
                          Run Once
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="frequency"
                          id="frequency-daily"
                          value="daily"
                          checked={frequency === "daily"}
                          onChange={(e) => setFrequency(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="frequency-daily">
                          Daily
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="frequency"
                          id="frequency-weekly"
                          value="weekly"
                          checked={frequency === "weekly"}
                          onChange={(e) => setFrequency(e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="frequency-weekly">
                          Weekly
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsLaunchModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleLaunchSimulation}
                  >
                    Launch Simulation
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RedTeaming; 